import { Button, Card, TextField, Typography } from '@mui/material'
import PublicPageWrapper from 'components/wrappers/PublicPageWrapper'
import Head from 'next/head'
import Image from 'next/image'
import styles from './pricing.module.scss'
import customerSatisfactionImage from '../public/images/index/index-customer-satisfaction.png'
import Link from 'next/link'

export default function Home() {
  return (
    <div>
      <Head>
        <title>MyTags | Pricing</title>
        <link rel="icon" href="/favicon.ico" />
        <script defer data-domain="mytags.info" src="https://plausible.io/js/plausible.js"></script>
      </Head>

      <PublicPageWrapper>

      <section className={`${styles.pricingIntro} ${styles.container}`}>
          <Typography variant='h1' textTransform='uppercase' fontSize={64}>
            Sign up now
          </Typography>
          <Typography variant='subtitle1' fontSize={18}>
            MyTags is FREE to sign up for and we require no credit card up front. If you outgrow our FREE plan you can choose to upgrade to one our paid plans listed below.
          </Typography>
        <section className={`${styles.pricingPlansV2} ${styles.container}`}>

            <div className={styles.plansContainer}>

              <article className={styles.planCard}>
                <Typography variant='overlineSmaller'>

                </Typography>
                <div className={styles.planTitle}>
                  <Typography variant='h2'>
                    FREE
                  </Typography>
                </div>
                <Typography variant='subtitle2'>
                  Get essential features with our FREE plan.
                </Typography>
                <div className={styles.planFeatures}>
                  <p><strong>1</strong> Tag Group</p>
                  <p><strong>2</strong> QR Tags</p>
                  <p><strong>20</strong> Tag views per day</p>
                  <p>Export QR Code as a PDF document</p>
                </div>
              </article>
            </div>
            </section>

          <Link href='/create-account' passHref>
            <Button
              variant='contained'
              color='primary'
              disableElevation
              size='large'
              sx={{
                maxWidth: '100%',
                width: 420,
                marginTop: 3
              }}
            >
              Start your FREE plan now!
            </Button>
          </Link>
          <Typography variant='subtitle1' color='primary' sx={{ mt: 3 }}>
            No Credit Card required for signing up!
          </Typography>
        </section>

        <section className={`${styles.pricingPlansV2} ${styles.container}`}>

          <div className={styles.plansGroup}>

            <div className={styles.description}>
              <Typography variant='h2'>
                Monthly plans
              </Typography>
              <Typography>
                These plans allow you to pay for MyTags per month. You can cancel your subscription at any point.
              </Typography>
            </div>

            <span className={styles.visualSeparator} />

            <div className={styles.plansContainer}>

              <article className={styles.planCard}>
                <Typography variant='overlineSmaller'>
                  Start Small
                </Typography>
                <div className={styles.planTitle}>
                  <Typography variant='h2'>
                    Basic
                  </Typography>
                  <div className={styles.planPriceContainer}>
                    <span className={styles.planPrice}>€5</span>
                    <span className={styles.planPriceCadence}>/month</span>
                    <span className={styles.planPriceNote}>+ VAT if applicable</span>
                  </div>
                </div>
                <Typography variant='subtitle2'>
                  Everything from the FREE plan with increased limits and more features.
                </Typography>
                <ul className={styles.planFeatures}>
                  <li><strong>1</strong> Tag Group</li>
                  <li><strong>3</strong> Tags per Tag Group</li>
                  <li><strong>50</strong> Tag views per day</li>
                  <li>Export QR Code as a PDF document</li>
                  <li>Export Tag Content as a PDF document</li>
                </ul>
              </article>

              <article className={styles.planCard}>
                <Typography variant='overlineSmaller'>
                  More content and visits
                </Typography>
                <div className={styles.planTitle}>
                  <Typography variant='h2'>
                    Basic+
                  </Typography>
                  <div className={styles.planPriceContainer}>
                    <span className={styles.planPrice}>€15</span>
                    <span className={styles.planPriceCadence}>/month</span>
                    <span className={styles.planPriceNote}>+ VAT if applicable</span>
                  </div>
                </div>
                <Typography variant='subtitle2'>
                  Tag Download and increased usage.
                </Typography>
                <ul className={styles.planFeatures}>
                  <li><strong>5</strong> Tag Group</li>
                  <li><strong>15</strong> Tags per Tag Group</li>
                  <li><strong>250</strong> Tag views per day</li>
                  <li>Export QR Code as a PDF document</li>
                  <li>Export Tag Content as a PDF document</li>
                  <li>Offline use of <strong>Tag content</strong> for your users (downloadable as Image or PDF)</li>
                </ul>
              </article>

            </div>

          </div>

          <div className={styles.plansGroup}>

            <div className={styles.description}>
              <Typography variant='h2'>
                Yearly plans
              </Typography>
              <Typography>
                With these plans you pay for MyTags on a yearly basis. They offer more features, higher usage limits and have no traffic limitations.
              </Typography>
            </div>

            <span className={styles.visualSeparator} />

            <div className={styles.plansContainer}>

              <article className={styles.planCard}>
                <Typography variant='overlineSmaller'>
                  Small & Medium Business
                </Typography>
                <div className={styles.planTitle}>
                  <Typography variant='h2'>
                    Premium
                  </Typography>
                  <div className={styles.planPriceContainer}>
                    <span className={styles.planPrice}>€150</span>
                    <span className={styles.planPriceCadence}>/year</span>
                    <span className={styles.planPriceNote}>+ VAT if applicable</span>
                  </div>
                </div>
                <Typography variant='subtitle2'>
                  Enjoy a rich feature set with no traffic limitations.
                </Typography>
                <ul className={styles.planFeatures}>
                  <li><strong>10</strong> Tag Group</li>
                  <li><strong>20</strong> Tags per Tag Group</li>
                  <li><strong>Unlimited</strong> Tag views per day</li>
                  <li>Export QR Code as a PDF document</li>
                  <li>Export Tag Content as a PDF document</li>
                  <li>Offline use of <strong>Tag content</strong> for your users (downloadable as Image or PDF)</li>
                  <li><strong>Multiple users</strong> for managing your Tags</li>
                </ul>
              </article>

              <article className={styles.planCard}>
                <Typography variant='overlineSmaller'>
                  Events, venues, large rentals and hotels
                </Typography>
                <div className={styles.planTitle}>
                  <Typography variant='h2'>
                    Professional
                  </Typography>
                  <div className={styles.planPriceContainer}>
                    <span className={styles.planPrice}>€250</span>
                    <span className={styles.planPriceCadence}>/year</span>
                    <span className={styles.planPriceNote}>+ VAT if applicable</span>
                  </div>
                </div>
                <Typography variant='subtitle2'>
                  Make tags feel your own with branding, track usage with analytics.
                </Typography>
                <ul className={styles.planFeatures}>
                  <li><strong>20</strong> Tag Group</li>
                  <li><strong>40</strong> Tags per Tag Group</li>
                  <li><strong>Unlimited</strong> Tag views per day</li>
                  <li>Export QR Code as a PDF document</li>
                  <li>Export Tag Content as a PDF document</li>
                  <li>Offline use of <strong>Tag content</strong> for your users (downloadable as Image or PDF)</li>
                  <li><strong>Multiple users</strong> for managing your Tags</li>
                  <li>Visitor <strong>analytics</strong> per Tag</li>
                  <li>Your own <strong>branding</strong> (colors, logo)</li>
                </ul>
              </article>

            </div>

          </div>

        </section>


        <section className={`${styles.pricingFeaturesCta} ${styles.container}`}>
          <span className={styles.ctaAroundLine}></span>
          <Link href='/create-account'>
            <Button
              variant='outlined'
              color='primary'
              disableElevation
              size='large'
              sx={{
                width: '100%',
                maxWidth: 420,
                minWidth: 240,
                margin: 0,
              }}
            >
              Sign up now!
            </Button>
          </Link>
          <span className={styles.ctaAroundLine}></span>
        </section>

        <section className={`${styles.pricingMessage} ${styles.container}`}>
          <Typography variant='subtitle1' textAlign='center'>Got questions about our Plans or Pricing? Don’t hesitate to <Link href='/contact-us'>Contact us!</Link></Typography>
        </section>

        <div className={styles.presentationStripe}>
          <section className={`${styles.ctaBoxStripe} ${styles.containerWide}`}>
            <Card>
              <div className={styles.ctaBoxStripeCard}>
                <div></div>
                <div className={`${styles.ctaBoxStripeContent} ${styles.container}`}>
                  <div>
                    <Typography variant='h2' color='common.white'>
                      Start elevating your rental business <u>now</u> and delight your visitors
                    </Typography>
                    <Typography variant='subtitle2' color='common.white'>
                      Answer all your Visitor's questions with just a few clicks. Sign up now for FREE (NO credit card required).
                    </Typography>
                  </div>
                  <Link href='/create-account' passHref>
                    <Button
                      variant='contained'
                      color='primary'
                      fullWidth
                      disableElevation
                      size='large'
                    >
                      Sign up now
                    </Button>
                  </Link>
                </div>
                <div className={styles.ctaBoxGraphic}>
                  <Image
                    src={customerSatisfactionImage}
                    alt='Scan the QR code to see the Tag!'
                    layout='responsive'
                    sizes="(min-width: 75em) 33vw,
                        (min-width: 48em) 50vw,
                        100vw"
                  />
                </div>
              </div>
            </Card>
          </section>
        </div>

      </PublicPageWrapper>
    </div>
  )
}

export async function getStaticProps() {

  return {
    props: {},
    revalidate: 10,
  }
}
